/**
 * ReportAttractionsPageNew — spec §5.6 with trip summary header, thicker bars, count per line.
 */
import React from 'react';
import { useTripAttractionReports } from '../hooks/useReports';
import { SkeletonRow } from '../design-system/primitives/SkeletonRow';
import { EmptyState } from '../design-system/primitives/EmptyState';
import { BarChart3 } from 'lucide-react';

export function ReportAttractionsPageNew(): React.ReactElement {
  const { data: reports, isLoading } = useTripAttractionReports();

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Attraction Selections</h1>
      {isLoading ? (
        <SkeletonRow count={4} width="100%" />
      ) : !reports?.length ? (
        <EmptyState icon={BarChart3} iconColor="text-gray-400" title="No attraction data available" />
      ) : (
        <div className="max-w-3xl mx-auto space-y-6">
          {reports.map((trip) => {
            const totalVisits = trip.attractions.reduce((s, a) => s + a.count, 0);
            const parkCount = new Set(trip.attractions.map((a) => a.parkName)).size;
            const mostVisited = trip.attractions.sort((a, b) => b.count - a.count)[0];

            return (
              <div key={trip.tripId}>
                {/* Trip summary header — spec §5.6 */}
                <div className="mb-4">
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{trip.tripName}</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {totalVisits} attractions visited • {parkCount} parks • most visited: {mostVisited?.attractionName} ({mostVisited?.count} times)
                  </p>
                </div>

                {/* Grouped bars by park */}
                {(() => {
                  const byPark: Record<string, typeof trip.attractions> = {};
                  trip.attractions.forEach((a) => {
                    (byPark[a.parkName] = byPark[a.parkName] || []).push(a);
                  });
                  return Object.entries(byPark).map(([park, attrs]) => {
                    const maxCount = Math.max(...attrs.map((a) => a.count));
                    return (
                      <div key={park} className="mb-4">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{park} ({attrs.length})</h3>
                        <div className="space-y-1">
                          {attrs.map((a) => {
                            const barWidth = maxCount > 0 ? Math.round((a.count / maxCount) * 100) : 0;
                            return (
                              <div key={a.attractionId} className="flex items-center gap-3">
                                <span className="text-sm text-gray-900 dark:text-gray-100 w-48 truncate">{a.attractionName}</span>
                                <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-3 relative">
                                  <div className="bg-disney-blue-500 h-3 rounded-full transition-all" style={{ width: `${barWidth}%` }} />
                                </div>
                                <span className="text-sm font-mono text-gray-500 dark:text-gray-400 w-8 text-right">{a.count}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
